import * as sentry from "@sentry/react";
import { createRoot } from "react-dom/client";

import RootErrorBoundary from "Flocknote/RootErrorBoundary";

import FlocknoteApp from "Flocknote";

import { getSentryEnvironment } from "./Utilities/Sentry/getSentryEnvironment";
import { getSentrySampleRates } from "./Utilities/Sentry/getSentrySampleRates";
import { isSentryEnabled } from "./Utilities/Sentry/isSentryEnabled";
import { routeNameFromPath } from "./Utilities/Sentry/routeNameFromPath";
import { markPointerType } from "./Utilities/pointerType";
import redirectProfileHashToParam from "./Utilities/redirectProfileHashToParam";
import retryChunkLoads from "./Utilities/retryChunkLoads";
import "./index.scss";

// Retries JS/CSS chunk downloads that fail (a couple tries, then gives up). Needs
// to run before anything lazy renders.
retryChunkLoads();

// Convert a legacy profile-tab hash (e.g. #giving) into ?profileTab=giving before
// react-router reads the URL on first render. Must run before root.render().
redirectProfileHashToParam();

markPointerType();

const { tracesSampleRate, profileSessionSampleRate } = getSentrySampleRates();

sentry.init({
  dsn: "https://f58ae101bf0f6cad2520590daeaa153b@o4508342913466368.ingest.us.sentry.io/4508342915563520",
  // Off for personal/local dev hosts so their in-flux errors don't flood Sentry.
  enabled: isSentryEnabled(),
  // at 0 these still hook fetch/XHR/history for nothing, so skip them
  integrations:
    tracesSampleRate > 0
      ? [
          sentry.browserTracingIntegration({
            beforeStartSpan: (options) => ({
              ...options,
              name: routeNameFromPath(options.name),
            }),
          }),
          sentry.browserProfilingIntegration(),
        ]
      : [],
  environment: getSentryEnvironment(),
  release: `fnreact_preNavRework`,
  tracesSampleRate,
  // decided once per tab, and only traced pages in that tab get profiled
  profileSessionSampleRate,
  // profiles automatically during traced page loads/navigations.
  profileLifecycle: "trace",
  ignoreErrors: [
    /^Non-Error promise rejection captured with value?/,
    /^cordova already defined?/,
    //Fixes FLOCKNOTE-REACT-WD5 (recaptcha error that doesn't affect users)
    "Missing required parameters: sitekey",
  ],
  // for even more control over what to send, use beforeSend: return null to not send
  // beforeSend(event) {
  //   console.log(event);
  //   return event;
  // },
});

// Expose the sentry module (not the init() return value) so that setContext,
// setUser, captureException, etc. are all available via window.Sentry.
window.Sentry = sentry;

const container = document.getElementById("fnReactHolder");
const root = createRoot(container);

root.render(
  <RootErrorBoundary>
    <FlocknoteApp />
  </RootErrorBoundary>
);
